import { useState } from "react"
import type { CSSProperties, FC } from "react"
import { Tooltip } from "antd"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import chevronDownIcon from "assets/icons/quotation-viewer/chevron-down.svg"
import { CheckmarkIcon } from "components/ui/icons/checkmark/CheckmarkIcon"
import { compactTooltipStyles, getShadowTooltipContainer } from "../shadowTooltip"
import styles from "./FontSizePicker.module.scss"

type Props = {
	value: number
	onChange: (size: number) => void
	options: readonly number[]
}

const popoverStyle: CSSProperties = {
	display: "flex",
	flexDirection: "column",
	width: 120,
	maxHeight: 240,
	overflowY: "auto",
	padding: 4,
	background: "#fff",
	border: "1px solid var(--clr-lightGray, #dee0eb)",
	borderRadius: 8,
	boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
}

const rowStyle = (selected: boolean): CSSProperties => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 8,
	width: "100%",
	padding: "6px 8px",
	border: "none",
	borderRadius: 6,
	background: selected ? "var(--clr-superLightGray, #f2f3f7)" : "transparent",
	cursor: "pointer",
	fontSize: 14,
	textAlign: "left",
})

/**
 * Font-size preset dropdown for the note tool-options bar (REQ-395/396): a plain vertical list of
 * presets with the active one checked — confirmed against Figma (`local-docs/plans/figma-notes.md`
 * frame `18916:553778`) to be a plain preset list, NOT a slider, so this does not reuse
 * `SliderPopover` (which wasn't ported onto this branch for that reason). Follows
 * `ColorSwatchPicker`'s popover shell (same `popupRender` + inline-styles approach, same reasoning)
 * with a checkmark affordance borrowed from the Shapes menu's `shapeMenuCheck` precedent described
 * in AGENTS.md.
 */
export const FontSizePicker: FC<Props> = ({ value, onChange, options }) => {
	const [open, setOpen] = useState(false)

	return (
		<Dropdown
			items={[]}
			placement="bottomLeft"
			open={open}
			onOpenChange={setOpen}
			trigger={["click"]}
			overlayStyle={{ zIndex: 1500 }}
			popupRender={() => (
				<div style={popoverStyle}>
					{options.map((size) => (
						<button
							key={size}
							type="button"
							style={rowStyle(size === value)}
							onClick={() => {
								onChange(size)
								setOpen(false)
							}}
						>
							{size}
							{size === value ? (
								<span style={{ width: 14, height: 14, color: "var(--clr-main, #0047ff)" }}>
									<CheckmarkIcon />
								</span>
							) : (
								<span style={{ width: 14 }} />
							)}
						</button>
					))}
				</div>
			)}
		>
			<Tooltip
				title="Font size"
				placement="bottom"
				getPopupContainer={getShadowTooltipContainer}
				styles={compactTooltipStyles}
			>
				<button type="button" className={styles.trigger} aria-label="Font size">
					{value}
					<img className={styles.chevron} src={chevronDownIcon} alt="" />
				</button>
			</Tooltip>
		</Dropdown>
	)
}
